import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { ArrowLeft, ArrowRight } from "lucide-react";
import {
  markNotificationAsRead,
  markAllNotificationsAsRead,
  fetchInAppNotificationsFull,
} from "src/services/NotificationAnnouncementsAPIs";
import { useNotification } from "../../../contexts/NotificationContext";
import { useMenu } from "src/contexts/MenuContext";
import { getApiLangFromI18n } from "src/utils/apiLang";
import { InAppNotification } from "src/layouts/MyAccount/Notifications/NotificationInterfaces";
import { filterToday, filterThisWeek } from "src/layouts/MyAccount/Notifications/NotificationFilters";
import NotificationCategoryIcon from "src/components/reusable/NotificationCategoryIcon";
import NotificationStateDisplay from "src/components/reusable/NotificationStateDisplay";

// Recent notifications shown in the popup; the rest are one "View More" away.
const POPUP_LIMIT = 15;

// Mirrors the "All Notifications" entry's key in SmartSearch's `pageMap`
// (src/components/reusable/SmartSearch.tsx) — the only place these
// MenuContext keys are otherwise assigned meaning. Both actions below
// navigate to that same route, so both must set this same key.
const ALL_NOTIFICATIONS_MENU_KEY = "52";

type GroupKey = "today" | "thisWeek" | "earlier";

const groupNotifications = (list: InAppNotification[]): Record<GroupKey, InAppNotification[]> => {
  const today = filterToday(list);
  const todayIds = new Set(today.map(n => n.notification_id));
  const thisWeek = filterThisWeek(list).filter(n => !todayIds.has(n.notification_id));
  const thisWeekIds = new Set(thisWeek.map(n => n.notification_id));
  const earlier = list.filter(n => !todayIds.has(n.notification_id) && !thisWeekIds.has(n.notification_id));
  return { today, thisWeek, earlier };
};

const NotificationsPopUp: React.FC<{ onClose: () => void }> = ({ onClose }) => {
  const { t, i18n } = useTranslation();
  const isArabic = i18n.language?.startsWith("ar");
  const ViewMoreArrowIcon = isArabic ? ArrowLeft : ArrowRight;
  const { refreshCount, syncUnreadWithFetchedList } = useNotification();
  const notificationPopUpRef = useRef<HTMLDivElement>(null);
  const [notifications, setNotifications] = useState<InAppNotification[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [isMarkingAllRead, setIsMarkingAllRead] = useState(false);
  const navigate = useNavigate();
  const [expandedNotifications, setExpandedNotifications] = useState<Set<number>>(new Set());
  const { setSelectedMenuItem } = useMenu();

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        notificationPopUpRef.current &&
        !notificationPopUpRef.current.contains(event.target as Node)
      ) {
        onClose();
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [onClose]);

  useEffect(() => {
    const fetchNotifications = async () => {
      try {
        const data = await fetchInAppNotificationsFull({
          page: 1,
          page_size: POPUP_LIMIT,
          lang: getApiLangFromI18n(i18n),
        });
        setNotifications(data.notifications);
        setTotal(data.pagination.totalRecords);
        // `unread_count` is the account-wide total, not just this capped-at-15
        // fetch, so the bell badge doesn't undercount older unread items.
        syncUnreadWithFetchedList(data.unread_count);
      } catch {
        setError(t("notificationsPopup.fetchError", { defaultValue: "There was an error fetching the notifications." }));
      } finally {
        setLoading(false);
      }
    };

    fetchNotifications();
  }, [i18n, i18n.language, i18n.resolvedLanguage]);

  const markAsReadAndRefresh = async (id: number) => {
    try {
      const success = await markNotificationAsRead(id);
      if (success) {
        setNotifications(prev =>
          prev.map(n => (n.notification_id === id ? { ...n, is_read: true } : n))
        );
        await refreshCount();
      }
    } catch {
      console.error("Failed to mark notification as read.");
    }
  };

  const handleMarkAllRead = async () => {
    if (isMarkingAllRead) {return;}
    setIsMarkingAllRead(true);
    try {
      const success = await markAllNotificationsAsRead(getApiLangFromI18n(i18n));
      if (success) {
        setNotifications(prev => prev.map(n => ({ ...n, is_read: true })));
        await refreshCount();
      }
    } catch {
      console.error("Failed to mark all notifications as read.");
    } finally {
      setIsMarkingAllRead(false);
    }
  };

  // Clicking a notification now opens the full list scrolled to that item,
  // instead of jumping straight to its source dashboard.
  const handleNotificationClick = async (notification: InAppNotification) => {
    if (!notification.is_read) {
      await markAsReadAndRefresh(notification.notification_id);
    }
    setSelectedMenuItem(ALL_NOTIFICATIONS_MENU_KEY);
    onClose();
    navigate("/account/my-account/all-notifications", {
      state: {
        targetNotificationId: notification.notification_id,
        createdAt: notification.created_at,
      },
    });
  };

  const handleViewAllClick = () => {
    setSelectedMenuItem(ALL_NOTIFICATIONS_MENU_KEY);
    onClose();
    navigate("/account/my-account/all-notifications");
  };

  const toggleExpand = async (notification: InAppNotification) => {
    const id = notification.notification_id;
    setExpandedNotifications(prev => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });

    if (!notification.is_read) {
      await markAsReadAndRefresh(id);
    }
  };

  const getContent = (notification: InAppNotification) =>
    notification.content || notification.notification_content || "";

  const renderRow = (notification: InAppNotification) => {
    const text = getContent(notification);
    const words = text.split(" ");
    const isLong = words.length > 15;
    const isExpanded = expandedNotifications.has(notification.notification_id);
    const created = new Date(notification.created_at);
    const unreadLabel = t("allNotifications.unread", { defaultValue: "Unread" });
    const rowLabel = `${notification.category}: ${text}${notification.is_read ? "" : ` (${unreadLabel})`}`;

    // Stays a <div> (not a <button>) because it wraps the nested "View
    // More"/"View Less" toggle below, which is itself a real <button> —
    // buttons can't contain interactive content. role="button" + tabIndex +
    // onKeyDown keep it keyboard-operable; the nested toggle stops
    // propagation so activating it doesn't also fire the row's own handler.
    return (
      <div
        key={notification.notification_id}
        className={`all-notification-row ${notification.is_read ? "" : "all-notification-row--unread"}`}
        role="button"
        tabIndex={0}
        aria-label={rowLabel}
        onClick={() => handleNotificationClick(notification)}
        onKeyDown={event => {
          if (event.key === "Enter" || event.key === " ") {
            event.preventDefault();
            handleNotificationClick(notification);
          }
        }}
      >
        <NotificationCategoryIcon category={notification.category} />
        <div className="all-notification-row__body">
          <span className="all-notification-row__title">{notification.category}</span>
          <span className="all-notification-row__description">
            {!isLong ? text : (
              <>
                {isExpanded ? text : words.slice(0, 15).join(" ") + "... "}
                <button
                  type="button"
                  className="notifications-popup-row__toggle"
                  onClick={e => { e.stopPropagation(); toggleExpand(notification); }}
                  onKeyDown={e => e.stopPropagation()}
                >
                  {isExpanded
                    ? t("notificationsPopup.viewLess", { defaultValue: "View Less" })
                    : t("notificationsPopup.viewMoreInline", { defaultValue: "View More" })}
                </button>
              </>
            )}
          </span>
          <span className="all-notification-row__meta">
            {!notification.is_read && (
              <span className="all-notification-row__unread-dot" aria-hidden />
            )}
            {`${String(created.getDate()).padStart(2, "0")}-${String(created.getMonth() + 1).padStart(2, "0")}-${created.getFullYear()}`}
            {" | "}
            {created.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit", hour12: true })}
          </span>
        </div>
      </div>
    );
  };

  const groups = groupNotifications(notifications);
  const groupOrder: { key: GroupKey; labelKey: string; defaultLabel: string }[] = [
    { key: "today", labelKey: "notificationsPopup.groups.today", defaultLabel: "Today" },
    { key: "thisWeek", labelKey: "notificationsPopup.groups.thisWeek", defaultLabel: "This Week" },
    { key: "earlier", labelKey: "notificationsPopup.groups.earlier", defaultLabel: "Earlier" },
  ];

  return (
    <div ref={notificationPopUpRef} className="notifications-modal">
      <div className="notifications-modal-header">
        <span className="notifications-modal-title">
          {error
            ? t("notificationsPopup.titleError", { defaultValue: "Notifications" })
            : t("notificationsPopup.title", { count: total, defaultValue: `Notifications (${total})` })}
        </span>
        {!error && (
          <button
            type="button"
            className="notifications-mark-all-read"
            onClick={handleMarkAllRead}
            disabled={isMarkingAllRead || notifications.every(n => n.is_read)}
          >
            {t("notificationsPopup.markAllRead", { defaultValue: "Mark all as read" })}
          </button>
        )}
      </div>

      <div className="notifications-modal-content">
        {loading ? (
          <div className="loader-center">
            <div className="donut-spinner"></div>
          </div>
        ) : error ? (
          <NotificationStateDisplay
            variant="error"
            compact
            title={t("notificationsPopup.errorTitle", { defaultValue: "Something went wrong!" })}
            message={error}
          />
        ) : notifications.length > 0 ? (
          groupOrder.map(({ key, labelKey, defaultLabel }) => {
            const items = groups[key];
            if (items.length === 0) {return null;}
            return (
              <div key={key} className="notifications-popup-group">
                <div className="notifications-popup-group__label">
                  {t(labelKey, { defaultValue: defaultLabel })}
                </div>
                {items.map(renderRow)}
              </div>
            );
          })
        ) : (
          <NotificationStateDisplay
            variant="empty"
            compact
            title={t("notificationsPopup.emptyTitle", { defaultValue: "No Notifications" })}
            message={t("notificationsPopup.noNotifications")}
          />
        )}
      </div>

      {!loading && notifications.length > 0 && (
        <button type="button" className="notifications-view-more-btn" onClick={handleViewAllClick}>
          {t("notificationsPopup.viewMore", { defaultValue: "View More" })}
          <ViewMoreArrowIcon size={14} aria-hidden />
        </button>
      )}
    </div>
  );
};

export default NotificationsPopUp;
